import { type UserMeExEntity, type UserMeTenantExEntity } from '@/libs/api'
import { DC_ROLE, PARTNER_ROLE, USER_TYPE, type DcRole } from '@/libs/api/types'

/**
 * ログインユーザーのデフォルトテナントを取得
 */
export const getDefaultUserMeTenant = (
  user: UserMeExEntity
): UserMeTenantExEntity => user.tenants[0]

/**
 * 選択中の拠点(DC)における自分の DcRole を取得する。
 * 選択中の拠点に所属していない場合は undefined。
 */
export const getSelectedDcRole = (
  user: UserMeExEntity,
  selectedDcId: number
): DcRole | undefined =>
  // dcs はレスポンス拡張フィールドのため、旧レスポンスのキャッシュ等で欠ける可能性に備えて optional chaining にする
  getDefaultUserMeTenant(user)?.dcs?.find((dc) => dc.id === selectedDcId)?.role

/**
 * 選択中の拠点(DC)の拠点マネージャ(DcRole.MANAGER)かどうか。
 * システムマスタの新規登録・編集可否の判定に使う（UserRole.ADMIN の特例は設けない #3957）。
 */
export const isSelectedDcManager = (
  user: UserMeExEntity,
  selectedDcId: number
): boolean => getSelectedDcRole(user, selectedDcId) === DC_ROLE.MANAGER

/**
 * ログインユーザーのデフォルト拠点(DC)ID を取得する。
 * オーナーユーザーは所属DC、パートナーユーザーは先頭パートナーの所属DCを返す。
 * いずれの拠点にも紐づかない場合は undefined。
 */
export const getDefaultDcId = (user: UserMeExEntity): number | undefined => {
  const tenant = getDefaultUserMeTenant(user)
  return tenant.dc?.id ?? tenant.partners[0]?.dcId
}

/**
 * 記録済みの拠点 ID が、指定テナントで有効と確認できるときだけ返す。
 *
 * selectedDcIdAtom はテナント別ではなく localStorage の単一キーで、
 * 既定拠点も getDefaultUserMeTenant (先頭テナント) 由来のため、
 * 先頭以外のテナントを直接開くと別テナントの拠点 ID が入っている。
 * 確認できない場合は null を返し、呼び出し側で「拠点不明」として扱う。
 *
 * 判定に使うのは me が返すテナント情報だけで、拠点一覧の取得を待たない。
 * 一覧より狭くなる場合があるが、誤ったテナントへ帰属させるよりは
 * 記録しない側へ倒す。
 */
export const findTenantDcId = (
  user: UserMeExEntity,
  tenantKey: string,
  dcId: number | null
): number | null => {
  if (dcId === null) return null

  const tenant = user.tenants.find((t) => t.key === tenantKey)
  if (!tenant) return null

  const dcIds = [
    tenant.dc?.id,
    ...tenant.partners.map((partner) => partner.dcId)
  ].filter((id): id is number => typeof id === 'number')

  return dcIds.includes(dcId) ? dcId : null
}

/**
 * ドライバーのログインアカウントか（所属するパートナーでの役割がすべてドライバー）。
 * 見え方はパートナーユーザーと同じ（#5307）だが、マスタは直せないのでマスタの画面は出さない（#6014）
 */
export const isDriverAccount = (user: UserMeExEntity): boolean => {
  const partners = getDefaultUserMeTenant(user)?.partners ?? []
  return (
    partners.length > 0 &&
    partners.every((partner) => partner.role === PARTNER_ROLE.DRIVER)
  )
}

/**
 * 業務マスタ設定の画面のうち、パートナーユーザーが開けるもの（#6014）。
 * 自社の車両・ドライバーの一覧と詳細だけで、新規作成と取込の画面は開けない
 */
const PARTNER_OPENABLE_SETTINGS_PATH =
  /^\/[^/]+\/settings\/(trucks|drivers)(\/\d+)?\/?$/

/**
 * パートナーユーザーとして、自社の車両・ドライバーのマスタを開けるか（#6014）。
 * ほかの業務マスタはパートナーユーザーに見せない（#5602）。ドライバーのログインアカウントは開けない
 */
export const canOpenOwnMasters = (user: UserMeExEntity): boolean =>
  user.type === USER_TYPE.PARTNER && !isDriverAccount(user)

/** パートナーユーザーがこの業務マスタ設定の画面を開けるか */
export const canPartnerOpenSettingsPath = (
  user: UserMeExEntity,
  pathname: string
): boolean =>
  canOpenOwnMasters(user) && PARTNER_OPENABLE_SETTINGS_PATH.test(pathname)
